import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import PaymentForm from './PaymentForm';
import { stripePromise, stripeAppearance } from '../config/stripe';
import { createPaymentIntent, handleApiError } from '../services/api';
import { getPackageDetails } from '../config/stripe';

const PaymentWrapper = ({ 
  sessionId, 
  packageId = 'call_with_concierge', 
  customerInfo,
  formData,
  servicePrice = null,
  serviceInfo = null,
  onPaymentSuccess,
  onPaymentError,
  onPaymentProcessing 
}) => {
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get package details or use service price
  const packageDetails = servicePrice 
    ? { price: Math.round(servicePrice * 100), displayPrice: `$${servicePrice.toFixed(2)}` }
    : getPackageDetails(packageId);

  // Create payment intent on component mount
  useEffect(() => {
    if (!sessionId || !customerInfo.name || !customerInfo.email) {
      setError('Missing required customer information');
      setIsLoading(false);
      return;
    }

    const createIntent = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await createPaymentIntent(
          sessionId,
          packageId,
          packageDetails.price,
          customerInfo
        );

        setClientSecret(response.client_secret);
        setPaymentIntentId(response.payment_intent_id);
      } catch (err) {
        const errorMessage = handleApiError(err);
        setError(errorMessage);
        if (onPaymentError) {
          onPaymentError(err, errorMessage);
        }
      } finally {
        setIsLoading(false);
      }
    };

    createIntent();
  }, [sessionId, packageId, customerInfo.name, customerInfo.email]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="nc-payment-loading">
        <div className="nc-payment-spinner"></div>
        <p>Setting up payment...</p>
      </div>
    );
  }

  // Show error state if payment intent creation failed
  if (error || !clientSecret) {
    return (
      <div className="nc-payment-error">
        <div className="nc-error-icon">❌</div>
        <h3>Payment Setup Failed</h3>
        <p>{error || 'Failed to initialize payment'}</p>
        <button 
          type="button" 
          onClick={() => window.location.reload()} 
          className="nc-btn nc-btn-secondary"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Configure Elements with clientSecret
  const elementsOptions = {
    clientSecret,
    appearance: stripeAppearance,
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <PaymentForm
        sessionId={sessionId}
        packageId={packageId}
        customerInfo={customerInfo}
        formData={formData}
        serviceInfo={serviceInfo}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentError={onPaymentError}
        onPaymentProcessing={onPaymentProcessing}
        clientSecret={clientSecret}
        paymentIntentId={paymentIntentId}
      />
    </Elements>
  );
};

export default PaymentWrapper;
